import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import apiClient from '@/lib/apiClient';

// Poll Stripe payment status after redirect from checkout.
// On `paid`, the parent's `onPaid` callback fires (typically refetches the assessment
// to flip the UI from free → paid). Auto-stops after MAX_ATTEMPTS to avoid runaway.

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

const pollOnce = (sessionId) =>
  apiClient.get(`/payments/status/${sessionId}`).then(r => r.data);

// Module-level handlers — no closures over hook-local variables.
const handlePaid = (setStatus, onPaidRef) => {
  setStatus('paid');
  toast.success('Payment successful! Unlocking your Professional Report…');
  onPaidRef.current?.();
};

const handleExpired = (setStatus) => {
  setStatus('expired');
  toast.error('Payment confirmation timed out. Please refresh in a moment.');
};

const startPaymentPolling = (sessionId, setStatus, onPaidRef) => {
  const state = { cancelled: false, attempt: 0 };

  const advance = () => setTimeout(runTick, POLL_INTERVAL_MS);

  const onResponse = (response) => {
    if (state.cancelled) return;
    if (response.payment_status === 'paid') return handlePaid(setStatus, onPaidRef);
    if (response.status === 'expired' || state.attempt >= MAX_ATTEMPTS) return handleExpired(setStatus);
    advance();
  };

  const onFailure = () => {
    if (state.cancelled) return;
    if (state.attempt >= MAX_ATTEMPTS) { setStatus('error'); return; }
    advance();
  };

  const runTick = () => {
    if (state.cancelled) return;
    state.attempt += 1;
    pollOnce(sessionId).then(onResponse).catch(onFailure);
  };

  runTick();
  return () => { state.cancelled = true; };
};

const usePaymentPolling = ({ sessionId, onPaid }) => {
  const [status, setStatus] = useState('checking');
  // Latest onPaid in a ref so the polling loop never closes over a stale callback.
  const onPaidRef = useRef(onPaid);

  useEffect(() => { onPaidRef.current = onPaid; }, [onPaid, onPaidRef]);

  useEffect(() => {
    if (!sessionId) return undefined;
    return startPaymentPolling(sessionId, setStatus, onPaidRef);
  }, [sessionId, setStatus, onPaidRef, startPaymentPolling]);

  return status;
};

export default usePaymentPolling;
